import { AgencyService } from '@/services/agency-service'
import { html, attr, prop, Signal, TNode, When } from '@tempots/dom'
import { NavigationService } from '@tempots/ui'

/**
 * Guards the tenant-scoped routes (/:agencyId/*): if the signed-in user
 * is not a member of the agency in the URL — most commonly because they
 * were removed from it while it was still their preferred agency — send
 * them to /start, which picks another agency or walks them into
 * onboarding to create their own. Without this, every page under the
 * dead agency id greets them with a raw membership error.
 *
 * The check runs against the cached agency list first and re-fetches on
 * a miss, so a just-created agency (onboarding navigates here before the
 * cache knows about it) is not misread as a removal. On any lookup
 * failure it fails open — page-level error handling stays responsible.
 */
export function RequireAgencyMembership(
  { agencyId }: { agencyId: Signal<string> },
  children: () => TNode
): TNode {
  const allowed = prop(false)

  const isMember = async (id: string): Promise<boolean> => {
    try {
      let { agencies } = await AgencyService.listUserAgencies()
      if (!agencies.some(a => a.id === id)) {
        ;({ agencies } = await AgencyService.reloadUserAgencies())
      }
      return agencies.some(a => a.id === id)
    } catch {
      return true
    }
  }

  agencyId.on(id => {
    allowed.set(false)
    void isMember(id).then(member => {
      if (member) {
        allowed.set(true)
      } else {
        AgencyService.clearUserAgenciesCache()
        NavigationService.navigate('/start', { replace: true })
      }
    })
  })

  return When(allowed, children, () =>
    html.div(attr.class('p-8 text-sm text-gray-500'), 'Loading…')
  )
}
